import { arrayRemove, arrayUnion, doc, updateDoc } from "firebase/firestore";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import firebaseApp from "./firebase";
import { Collections, CollectionsClass } from "./firestore/collections";

let cachedWebFcmToken: string | null = null;
let foregroundListenerRegistered = false;

function vapidKey(): string | undefined {
    return process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim() || undefined;
}

/**
 * Saves the browser FCM token under `fcmTokens` (arrayUnion).
 * Mobile keeps using `fcmToken` (singular); Cloud Function sends to both.
 */
export async function registerWebFcmToken(userId: string): Promise<void> {
    if (typeof window === "undefined" || !userId) return;
    const key = vapidKey();
    if (!key) return;

    try {
        const supported = await isSupported();
        if (!supported) return;

        const messaging = getMessaging(firebaseApp);
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
        const token = await getToken(messaging, {
            vapidKey: key,
            serviceWorkerRegistration: registration,
        });
        if (!token) return;

        cachedWebFcmToken = token;
        const userRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
        await updateDoc(userRef, { fcmTokens: arrayUnion(token) });
    } catch {
        /* permission denied, unsupported, or missing config */
    }
}

/**
 * Removes this browser token from Firestore and invalidates it locally (best effort).
 */
export async function unregisterWebFcmToken(userId: string | undefined): Promise<void> {
    if (typeof window === "undefined" || !userId || !cachedWebFcmToken) return;
    const token = cachedWebFcmToken;
    cachedWebFcmToken = null;
    try {
        const messaging = getMessaging(firebaseApp);
        await deleteToken(messaging);
    } catch {
        /* ignore */
    }
    try {
        const userRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
        await updateDoc(userRef, { fcmTokens: arrayRemove(token) });
    } catch {
        /* signed out / rules */
    }
}

/**
 * Show a browser notification when a push arrives while the tab is in foreground.
 * Call once after app mounts (e.g. from App.tsx).
 */
export function initForegroundFcmListener(): void {
    if (typeof window === "undefined" || foregroundListenerRegistered) return;
    if (!vapidKey()) return;

    isSupported()
        .then((ok) => {
            if (!ok) return;
            foregroundListenerRegistered = true;
            const messaging = getMessaging(firebaseApp);
            onMessage(messaging, (payload) => {
                const title = payload.notification?.title || "Veshje për ty";
                const body = payload.notification?.body || "";
                if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                    new Notification(title, { body, icon: "/favicon.png" });
                }
            });
        })
        .catch(() => {});
}
